// Renders a stored date using the deployment's configured date format
// (Settings → Localisation → Date Format).
//
// Two defects made this necessary. `parseDate` in src/util/util.js hardcoded
// `d/M/yyyy`, so roughly fifteen surfaces — the family and pregnancy tables, the
// users table, the generated-link tables and the FHQ PDFs — ignored the setting
// entirely, while the date pickers and the moment-based pedigree surfaces
// honoured it. And it parsed with `new Date`, which reads a bare 'YYYY-MM-DD' as
// UTC MIDNIGHT and therefore rendered the previous day at any negative UTC
// offset.
//
// `parseDate` deliberately keeps its old fixed output: its one non-display
// caller, components/Medicare/medicareUtils.js, puts it in a claim file name and
// in the submitted payload, and a billing file must not change shape because an
// admin picked a different UI date format. Display goes through here instead.
//
// moment is used rather than date-fns because it takes the stored tokens
// ('DD/MM/YYYY', 'MM/DD/YYYY', 'DD.MM.YYYY') verbatim — no translation table to
// get out of step. DateInput needs such a table for date-fns, and that is
// exactly where the DD.MM.YYYY separator was silently turned into slashes.

import React from 'react';
import moment from 'moment';
import { ThemeContext } from 'src/store/ContextStore';
import { toLocalDate } from 'src/util/partialDate';

export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';

/**
 * Format a stored date for display. Exported un-hooked so it can be unit tested
 * and used outside a component.
 *
 * Parsing goes through `toLocalDate`, which reads 'YYYY-MM-DD' as literal
 * calendar components (no UTC round trip) and still copes with Date objects and
 * full ISO timestamps.
 *
 * @param {string|Date|null|undefined} value
 * @param {string} [dateFormat] moment tokens, e.g. 'DD/MM/YYYY'
 * @param {{ yearOnly?: boolean }} [options]
 *   `yearOnly` renders just the year — for groups configured to capture years
 *   only (saveDateAsYears), where showing a day and month would defeat the
 *   point of the setting.
 * @returns {string} '' when the value is absent or unreadable
 */
export const formatDateForDisplay = (
  value,
  dateFormat = DEFAULT_DATE_FORMAT,
  { yearOnly } = {}
) => {
  if (!value) return '';
  const parsed = toLocalDate(value);
  // Unreadable stored values (epoch-ms strings, "NULL", day-first text) degrade
  // to blank rather than "NaN/NaN/NaN" — the guard dateDisplayGuards.test.js
  // pins on parseDate, preserved here.
  if (!parsed) return '';
  if (yearOnly) return String(parsed.getFullYear());
  return moment(parsed).format(dateFormat || DEFAULT_DATE_FORMAT);
};

/**
 * Returns a formatter bound to the current theme's date format.
 *
 *   const fmtDate = useDateFormatter();
 *   fmtDate(member.dob);
 *   fmtDate(member.dob, { yearOnly: saveDateAsYears });
 *
 * Safe anywhere in the app: the ThemeContext provider is mounted in
 * Dashboard.jsx, which is the root component. Falls back to 'DD/MM/YYYY' before
 * the theme loads, matching GailScoreModal and SystemFeatures.
 */
export default function useDateFormatter() {
  const themeContext = React.useContext(ThemeContext);
  const dateFormat = themeContext?.theme?.dateFormat || DEFAULT_DATE_FORMAT;

  return React.useCallback(
    (value, options) => formatDateForDisplay(value, dateFormat, options),
    [dateFormat]
  );
}
